/**
 * The runtime art layer (RFC v0.7 §4, ADR-004): a JSON manifest + a resolver
 * that lets `Character` (and props/floors later) load REAL sprite packs from
 * `public/assets/`, with a **graceful fallback to the baked procedural
 * placeholders** when a pack is absent — the game never breaks on missing art.
 *
 * This module is Phaser-FREE on purpose: the parse + resolve logic is the
 * load-bearing part and is unit-tested headless. `PreloadScene` does the actual
 * Phaser texture loading and calls `markArtLoaded` for each pack that lands.
 *
 * Fail-loud contract (CLAUDE.md): an ABSENT manifest is a legitimately-empty
 * situation (→ all placeholders, no error). A PRESENT-but-malformed manifest is
 * an invalid input (→ `ManifestError`, field-named) — a dev bug caught in test,
 * never a silent wrong.
 */

export interface SpriteEntry {
  /** Path relative to `public/`, e.g. "assets/patients/weary.webp". */
  file: string;
  frameWidth: number;
  frameHeight: number;
  /** Normalized anchor of the character's feet within the frame (origin). */
  feetAnchor: { x: number; y: number };
  /**
   * Animation frame count (v0.9): when present the file is a HORIZONTAL
   * spritesheet of `frames` cells (each frameWidth×frameHeight) and the
   * loader uses `load.spritesheet` instead of `load.image`. Absent = a still.
   */
  frames?: number;
  /**
   * Asset pack (v0.11 manifest v2): which lazy-load group this sprite
   * ships in (office/detox/resi/php/iop/sober/alumni/core). REQUIRED at
   * version 2, absent at version 1 (a v1 manifest loads as one big pack).
   */
  pack?: string;
}

export interface SpriteManifest {
  version: number;
  sprites: Record<string, SpriteEntry>;
}

/** The CLOSED set of asset packs (v0.11 §3.2). Runtime validation
 * rejects a v2 manifest naming anything else — a typo'd pack would
 * otherwise strand its sprites as silent permanent placeholders
 * (v0.11 review: codex + swarm converged on this fail-loud gap). */
export const KNOWN_PACKS = ["core", "office", "detox", "resi", "php", "iop", "sober", "alumni"] as const;
export type PackId = (typeof KNOWN_PACKS)[number];

const SCENE_PACK: Record<string, PackId> = {
  office: "office",
  "tier-detox": "detox",
  "tier-residential": "resi",
  "tier-php": "php",
  "tier-iop": "iop",
  "tier-soberLiving": "sober",
  alumni: "alumni",
};

/** Which pack a scene tab needs before it opens (pure — testable headless).
 * CLOSED map: an unknown scene key throws field-named instead of casting
 * a typo into a phantom pack (v0.11 bot leg). */
export function packForScene(key: string): PackId {
  const pack = SCENE_PACK[key];
  if (pack === undefined) {
    throw new Error(`packForScene: unknown scene key ${JSON.stringify(key)} — expected one of ${Object.keys(SCENE_PACK).join("|")}`);
  }
  return pack;
}

export class ManifestError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ManifestError";
  }
}

/** v1 (one implicit pack) and v2 (explicit per-sprite packs) both load. */
const SUPPORTED_VERSIONS = [1, 2] as const;

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function requirePositiveInt(v: unknown, field: string): number {
  if (typeof v !== "number" || !Number.isInteger(v) || v < 1) {
    throw new ManifestError(`${field} must be a positive integer (got ${JSON.stringify(v)})`);
  }
  return v;
}

function requireUnit(v: unknown, field: string): number {
  if (typeof v !== "number" || Number.isNaN(v) || v < 0 || v > 1) {
    throw new ManifestError(`${field} must be a number in [0,1] (got ${JSON.stringify(v)})`);
  }
  return v;
}

/**
 * Validate + narrow a parsed-JSON value into a `SpriteManifest`. Throws
 * `ManifestError` (field-named) on anything malformed — never returns a
 * partial/degraded manifest.
 */
export function parseManifest(raw: unknown): SpriteManifest {
  if (!isObject(raw)) {
    throw new ManifestError(`manifest root must be an object (got ${typeof raw})`);
  }
  const version = raw.version;
  if (version !== 1 && version !== 2) {
    throw new ManifestError(`manifest.version must be one of ${SUPPORTED_VERSIONS.join("|")} (got ${JSON.stringify(version)})`);
  }
  if (!isObject(raw.sprites)) {
    throw new ManifestError("manifest.sprites must be an object");
  }
  const sprites: Record<string, SpriteEntry> = {};
  for (const [key, entry] of Object.entries(raw.sprites)) {
    if (!isObject(entry)) {
      throw new ManifestError(`sprites["${key}"] must be an object`);
    }
    if (typeof entry.file !== "string" || entry.file.length === 0) {
      throw new ManifestError(`sprites["${key}"].file must be a non-empty string`);
    }
    // Defense-in-depth: assets load same-origin under assets/ only — no
    // absolute URLs, schemes, or parent traversal (review finding, v0.9).
    if (!/^assets\/[\w./-]+\.(png|webp|json)$/.test(entry.file) || entry.file.includes("..")) {
      throw new ManifestError(`sprites["${key}"].file must be a relative assets/ path (got ${JSON.stringify(entry.file)})`);
    }
    if (!isObject(entry.feetAnchor)) {
      throw new ManifestError(`sprites["${key}"].feetAnchor must be an object`);
    }
    // v2 requires a pack on every sprite (fail-loud — a stray unpacked
    // sprite would silently never load); v1 must not carry one.
    if (version === 2 && !(KNOWN_PACKS as readonly string[]).includes(entry.pack as string)) {
      throw new ManifestError(`sprites["${key}"].pack must be one of ${KNOWN_PACKS.join("|")} at manifest version 2 (got ${JSON.stringify(entry.pack)})`);
    }
    sprites[key] = {
      file: entry.file,
      frameWidth: requirePositiveInt(entry.frameWidth, `sprites["${key}"].frameWidth`),
      frameHeight: requirePositiveInt(entry.frameHeight, `sprites["${key}"].frameHeight`),
      feetAnchor: {
        x: requireUnit(entry.feetAnchor.x, `sprites["${key}"].feetAnchor.x`),
        y: requireUnit(entry.feetAnchor.y, `sprites["${key}"].feetAnchor.y`),
      },
      // Optional animation sheet: validated when present, absent = a still.
      ...(entry.frames !== undefined
        ? { frames: requirePositiveInt(entry.frames, `sprites["${key}"].frames`) }
        : {}),
      ...(version === 2 ? { pack: entry.pack as string } : {}),
    };
  }
  return { version, sprites };
}

// --- runtime registry: which logical keys have a real texture loaded ---------

/** Manifest keys with this prefix form the varied PATIENT pool (RFC "many"). */
const PATIENT_PREFIX = "patient-";

const loaded = new Set<string>();
/** Loaded patient-pool keys, kept sorted so variant-picks are stable/order-free. */
const patientPool: string[] = [];

/** Phaser texture key a real pack is loaded under (namespaced off placeholders). */
export function artTextureKey(logicalKey: string): string {
  return `art/${logicalKey}`;
}

/** Called by PreloadScene once a pack's texture is confirmed resident. */
export function markArtLoaded(logicalKey: string): void {
  if (loaded.has(logicalKey)) return;
  loaded.add(logicalKey);
  if (logicalKey.startsWith(PATIENT_PREFIX)) {
    patientPool.push(logicalKey);
    patientPool.sort();
  }
}

export function isArtLoaded(logicalKey: string): boolean {
  return loaded.has(logicalKey);
}

/**
 * The one call sites use: given a logical texture key (e.g. "person-new"),
 * return the REAL art texture key if a pack loaded for it, else the logical key
 * itself (the baked placeholder). This is the whole fallback in one line.
 * Idempotent on already-resolved `art/…` keys (pool picks pass through Character).
 */
export function resolveCharacterTexture(logicalKey: string): string {
  if (logicalKey.startsWith("art/")) return logicalKey;
  return loaded.has(logicalKey) ? artTextureKey(logicalKey) : logicalKey;
}

/**
 * Pick a patient look from the loaded pool by a stable `variant` number (e.g. a
 * lead id) — same variant → same face across reloads, and no RNG-state touch
 * (the sprite is presentation, not sim). Falls back to the baked "person-new"
 * placeholder when no patient packs loaded.
 */
/** Stable pool pick shared by the patient + alumni resolvers (same index
 * math = same person across the whole continuum). */
function poolKey(variant: number): string | null {
  if (patientPool.length === 0) return null;
  return patientPool[Math.abs(Math.trunc(variant)) % patientPool.length]!;
}

export function resolvePatientTexture(variant: number): string {
  const key = poolKey(variant);
  return key === null ? "person-new" : artTextureKey(key);
}

/**
 * The SAME person, dressed better (Russell, 2026-07-20): alumni keep their
 * treatment-era face — the "alum-<variant>" pack is a wardrobe upgrade, not
 * a recast. Same index math as resolvePatientTexture so the identity holds;
 * falls back to the patient texture when the alum cut isn't shipped.
 */
export function resolveAlumniTexture(variant: number): string {
  const key = poolKey(variant);
  if (key === null) return "person-new";
  if (!key.startsWith(PATIENT_PREFIX)) return artTextureKey(key);
  const alumKey = "alum-" + key.slice(PATIENT_PREFIX.length);
  return isArtLoaded(alumKey) ? artTextureKey(alumKey) : artTextureKey(key);
}

/** Test-only: clear the registry between cases. */
export function _resetArtRegistry(): void {
  loaded.clear();
  patientPool.length = 0;
}
